import { createSelector } from 'reselect';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { ManualIncentiveEntry, AccessoryElement } from '@makemydeal/dr-platform-types';
import type { DraftDealProduct, StateTree } from '@makemydeal/dr-dash-types';
import { changeVehicleSelectors } from '../../store';
import type { RetentionDecisions } from '../../types';
import { buildCategories } from './buildCategories';
import type { CategoryConfig, CategoryKey } from './buildCategories';
import { combineRetainedTaxItems } from './combineRetainedTaxItems';

import type { RetentionTaxRow } from './retentionTaxTypes';

export const selectCategories = createSelector(
    [
        (state: unknown) => changeVehicleSelectors.additionalSelectors.getIncentivesForRetentionModal?.(state),
        (state: unknown) => changeVehicleSelectors.additionalSelectors.getAccessoriesForRetentionModal?.(state),
        (state: unknown) => changeVehicleSelectors.additionalSelectors.getVppForRetentionModal?.(state),
        (state: unknown) => changeVehicleSelectors.additionalSelectors.getManuallyOverriddenTaxItems?.(state),
        (state: unknown) => changeVehicleSelectors.additionalSelectors.getRetentionTaxContext?.(state)
    ],
    (incentives, accessories, vpp, taxes, taxContext): CategoryConfig[] =>
        buildCategories({
            incentives: incentives ?? [],
            vpp: vpp ?? [],
            accessories: accessories ?? [],
            taxes: taxes ?? [],
            taxContext: taxContext ?? { isLease: false, capped: false, crtCapped: false }
        })
);

export const buildRetentionDecisions = (
    categories: CategoryConfig[],
    isKept: (categoryKey: CategoryKey, itemId: string) => boolean,
    isPercentTaxRatesEnabled: boolean
): RetentionDecisions => {
    const decisions: RetentionDecisions = {
        incentives: [],
        vpp: [],
        accessories: [],
        taxes: []
    };

    categories.forEach((category) => {
        const keptIndices = category.items
            .map((item, index) => ({ id: item.id, index }))
            .filter(({ id }) => isKept(category.key, id))
            .map(({ index }) => index);

        if (category.key === 'incentives') {
            decisions.incentives = keptIndices.map((i) => category.rawItems[i] as ManualIncentiveEntry);
        } else if (category.key === 'vpp') {
            decisions.vpp = keptIndices.map((i) => category.rawItems[i] as DraftDealProduct);
        } else if (category.key === 'accessories') {
            decisions.accessories = keptIndices.map((i) => category.rawItems[i] as AccessoryElement);
        } else if (category.key === 'taxes') {
            const keptRows = keptIndices.map((i) => category.rawItems[i] as RetentionTaxRow);
            decisions.taxes = combineRetainedTaxItems(keptRows, isPercentTaxRatesEnabled);
        }
    });

    return decisions;
};

export const getKeepAllRetentionDecisions = (state: StateTree): RetentionDecisions | undefined => {
    const categories = selectCategories(state);
    if (categories.length === 0) {
        return undefined;
    }
    return buildRetentionDecisions(categories, () => true, featureToggleSelectors.isPercentTaxRatesEnabled(state));
};
